'use client'

import { useTranslations } from 'next-intl'

import { Box, ButtonPill, Col, ContentText, Row, spacing } from '@fortum/elemental-ui'

import type {
  CampaignConfigurationEntry,
  ContractHeroSectionEntry,
} from '@/shared/contentful/types'
import { useTheme } from '@/shared/hooks/useTheme'
import type {
  EnrichedContractProduct,
  EnrichedContractTemplate,
} from '@/shared/services/campaignDataResolver'

import { BulletList, ListItem } from '../../BulletList'
import { ContentfulImage } from '../../ContentfulImage'
import { Price } from '../../Price'
import { PricePortal } from '../../PricePortal'
import { PurchaseButton } from '../../PurchaseButton'
import { StyledHeading } from '../../StyledHeading'
import { StyledLink } from '../../StyledLink'
import { BasicHeroLayout } from '../BasicHeroLayout'
import { useContractHeroAnalytics } from './useContractHeroAnalytics'

export type ContractHeroProps = Omit<
  ContractHeroSectionEntry,
  'contract' | 'campaignConfiguration' | 'sys'
> & {
  contractTemplate: EnrichedContractTemplate
  contractProduct: EnrichedContractProduct
  campaignConfiguration: CampaignConfigurationEntry
}

/**
 * A hero component to be used in the section for contract pages.
 */
export const ContractHero: React.FC<ContractHeroProps> = (props) => {
  const { image, contractProduct, contractTemplate, campaignConfiguration, title, styleVariant } =
    props
  const {
    cfData: { title: productTitle },
  } = contractProduct
  const displayTitle = title ?? productTitle

  const { description, sellingPoints } = contractTemplate.cfData
  const { colors } = useTheme()
  const t = useTranslations('checkout.general')
  const uspList = sellingPoints?.map((sellingPoint, i) => (
    <ListItem key={i}>{sellingPoint}</ListItem>
  ))

  useContractHeroAnalytics({ contractTemplate })

  const isBannerStyle = styleVariant === 'banner'
  if (isBannerStyle) {
    const getSecondaryButton = () => {
      if (!contractProduct.cfData.url) {
        return null
      }

      return (
        <StyledLink href={contractProduct.cfData.url} data-testid="more-info-about-product-link">
          <ButtonPill status="secondary" invert transparent>
            {t('readMore')}
          </ButtonPill>
        </StyledLink>
      )
    }

    return (
      <BasicHeroLayout
        image={image}
        title={displayTitle}
        primaryButton={
          <PurchaseButton
            contractTemplate={contractTemplate}
            campaignConfiguration={campaignConfiguration}
            contractProduct={contractProduct}
            status="campaign"
          />
        }
        secondaryButton={getSecondaryButton()}
      >
        <Box display="flex" flexDirection="column" gap={spacing.xxs}>
          <PricePortal />
          <Price
            contractTemplate={contractTemplate}
            campaignConfiguration={campaignConfiguration}
            contractProduct={contractProduct}
            priceBreakdownMode="withFooter"
            shouldShowPricePlaceholder
            mode="minimal"
            align="left"
          />

          {description && <ContentText>{description}</ContentText>}
          {uspList && (
            <BulletList
              colors={{
                textPositive: colors.textLightOnDark,
              }}
            >
              {uspList}
            </BulletList>
          )}
        </Box>
      </BasicHeroLayout>
    )
  }

  return (
    <Row>
      <Col l={6} xl={5} marginBottom={{ default: spacing.xs, l: 'none' }}>
        {image && (
          <ContentfulImage
            wrapperProps={{
              position: 'relative',
              width: '100%',
              height: { default: '240px', m: '360px', l: '100%' },
            }}
            imageEntry={image}
            style={{ objectFit: 'contain', objectPosition: 'top center' }}
            priority
            maxWidth="900"
          />
        )}
      </Col>
      <Col l={6} xl={7} gap={spacing.xs} display="flex" flexDirection="column">
        <StyledHeading level={1}>{displayTitle}</StyledHeading>
        {description && <ContentText>{description}</ContentText>}
        {uspList && <BulletList>{uspList}</BulletList>}

        <PricePortal />

        <Price
          contractTemplate={contractTemplate}
          campaignConfiguration={campaignConfiguration}
          contractProduct={contractProduct}
          priceBreakdownMode="withFooter"
          shouldShowPricePlaceholder
        />

        <Box textAlign={{ default: 'center', m: 'left' }}>
          <PurchaseButton
            contractTemplate={contractTemplate}
            campaignConfiguration={campaignConfiguration}
            contractProduct={contractProduct}
          />
        </Box>
      </Col>
    </Row>
  )
}
